import { ServerSearchCombobox } from '@/components/ServerSearchCombobox';

// ─── PurchaseOrderCombobox ──────────────────────────────────────────────────────
// A select-only typeahead over GET /api/purchase-orders?q=. A thin adapter over
// ServerSearchCombobox. Rows already linked to an estimate show a muted
// "(linked to EST-XXXX)" note so the caller can warn before moving them.

/** The minimal shape handed back to the parent on selection. */
export interface PurchaseOrderPick {
  id: number;
  number: string;
  supplier_name: string;
  source_estimate_id: number | null;
  source_estimate_number: string | null;
}

interface PoSearchRow {
  id: number;
  number: string;
  supplier_name: string;
  source_estimate_id: number | null;
  source_estimate_number: string | null;
  status: string;
  total: number;
}

interface PurchaseOrdersSearchResponse {
  data: { purchase_orders: PoSearchRow[] };
}

interface PurchaseOrderComboboxProps {
  /** Selected PO id, or null. Controlled by the parent (drives input styling). */
  value: number | null;
  /** Called with the chosen PO, or null when the selection is cleared by typing. */
  onChange: (pick: PurchaseOrderPick | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function PurchaseOrderCombobox({
  value,
  onChange,
  disabled,
  placeholder,
}: PurchaseOrderComboboxProps) {
  return (
    <ServerSearchCombobox<PoSearchRow>
      value={value}
      onChange={(row) =>
        onChange(
          row
            ? {
                id: row.id,
                number: row.number,
                supplier_name: row.supplier_name,
                source_estimate_id: row.source_estimate_id,
                source_estimate_number: row.source_estimate_number,
              }
            : null,
        )
      }
      queryKey="po-picker"
      buildUrl={(q) => `/api/purchase-orders?q=${encodeURIComponent(q)}`}
      extract={(resp) =>
        (resp as PurchaseOrdersSearchResponse | undefined)?.data.purchase_orders ?? []
      }
      getKey={(p) => p.id}
      getLabel={(p) => `${p.number} · ${p.supplier_name}`}
      renderResult={(p) => (
        <>
          <span className="font-mono font-medium shrink-0">{p.number}</span>
          <span className="text-muted-foreground truncate">· {p.supplier_name}</span>
          {p.source_estimate_number && (
            <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
              (linked to {p.source_estimate_number})
            </span>
          )}
        </>
      )}
      disabled={disabled}
      placeholder={placeholder ?? 'Search purchase orders…'}
      promptMessage="Type to search purchase orders"
      emptyMessage="No matching purchase orders"
    />
  );
}
